import { Button } from "@components/ui/button"
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@components/ui/form"
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@components/ui/select"
import { useForm } from "react-hook-form"
import {
  useGetDropReasonsQuery,
  useRequestAccountDropMutation,
  useAccount,
} from "@apis/accounts/useAccounts"
import { accountKeys } from "@apis/accounts/queryKeys"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { useSendProspectEventMutation } from "@apis/prospects/useProspects"
import { Checkbox } from "@components/ui/checkbox"

type TDropRequestForm = {
  account_drop_reason_id: string
  drop_offer_code: string
  is_escalation: boolean
}

interface CancelAccountProps {
  account_id: string
}

export const CancelAccount = ({ account_id }: CancelAccountProps) => {
  const queryClient = useQueryClient()
  const getAccountQuery = useAccount(account_id)
  const prospect_guid = getAccountQuery.data?.prospect_guid
  const getDropReasonsQuery = useGetDropReasonsQuery()
  const requestAccountDropMutation = useRequestAccountDropMutation()
  const sendProspectEventMutation = useSendProspectEventMutation()

  const [showForm, setShowForm] = useState(false)
  const [dropConfirmationCode, setDropConfirmationCode] = useState<
    string | null
  >(null)

  const form = useForm<TDropRequestForm>({
    mode: "onTouched",
    defaultValues: {
      account_drop_reason_id: "",
      drop_offer_code: "keepCurrent",
    },
  })

  if (getDropReasonsQuery.isPending || getAccountQuery.isPending) {
    return (
      <div className="tw:flex tw:justify-center">
        <Loader2 className="tw:h-6 tw:w-6 tw:animate-spin tw:text-gray-500" />
      </div>
    )
  }

  if (getDropReasonsQuery.isError) {
    return (
      <div className="tw:text-red-500">
        Error loading drop reasons. {getDropReasonsQuery.error?.message}
      </div>
    )
  }

  const handleSubmit = (data: TDropRequestForm) => {
    requestAccountDropMutation.mutate(
      {
        account_id,
        request: {
          snooze_winbacks: true, // setting this prop to true will trigger the snooze logic in backend
          is_escalation: data.is_escalation,
          account_drop_reason_id: Number(data.account_drop_reason_id),
          drop_offer_code:
            data.drop_offer_code === "keepCurrent"
              ? null
              : data.drop_offer_code,
        },
      },
      {
        onSuccess: (res) => {
          queryClient.invalidateQueries({
            queryKey: accountKeys.accountEvents(prospect_guid, account_id),
          })
          queryClient.invalidateQueries({
            queryKey: accountKeys.accountNotifications(
              prospect_guid,
              account_id,
            ),
          })
          setDropConfirmationCode(res.confirmation_code)
        },
        onError: (error) => {
          toast.error(`Error requesting drop! Error: ${error}`, {
            duration: 10000,
          })
        },
      },
    )
  }

  const handleSendProspectEmail = () => {
    sendProspectEventMutation.mutate(
      {
        prospect_guid: prospect_guid as string,
        request: {
          event_name: "send_cancellation_email",
          payload: {
            account_id: account_id,
          },
        },
      },
      {
        onSuccess: () => toast.success("Drop confirmation email request sent"),
        onError: (error) =>
          toast.error(
            `Drop confirmation email request failed. Error: ${error}`,
          ),
      },
    )
  }

  return (
    <>
      {!showForm ? (
        <Button variant="destructive" onClick={() => setShowForm(true)}>
          Cancel Account
        </Button>
      ) : (
        <>
          {dropConfirmationCode ? (
            <div className="tw:flex tw:flex-col tw:gap-3">
              <h4 className="tw:text-xl tw:font-semibold">
                Drop request was successfully processed
              </h4>
              <p>Confirmation Code: {dropConfirmationCode}</p>
              {/* this button should only be shown for valid email - need backend ticket */}
              <Button
                className="tw:self-start"
                onClick={handleSendProspectEmail}
                disabled={sendProspectEventMutation.isPending}
              >
                Send Cancellation Email
                {sendProspectEventMutation.isPending && (
                  <Loader2 className="tw:animate-spin" />
                )}
              </Button>
              <p>
                *Note: Sending a Cancellation Email does not confirm email is
                valid
              </p>
            </div>
          ) : (
            <div className="tw:flex tw:flex-col tw:gap-4">
              <h4 className="tw:text-xl tw:font-semibold">
                Are you sure you want to cancel the Member?
              </h4>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)}>
                  <div className="tw:flex tw:flex-col tw:gap-6">
                    <FormField
                      control={form.control}
                      name="account_drop_reason_id"
                      rules={{ required: "Drop reason is required" }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Reason for Cancellation</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select Drop Reason" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {getDropReasonsQuery.data?.map((reason) => (
                                <SelectItem
                                  key={reason.description}
                                  value={reason.account_drop_reason_id.toString()}
                                >
                                  {reason.description}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="is_escalation"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Escalation?</FormLabel>
                          <FormControl>
                            <Checkbox onCheckedChange={field.onChange} />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <p className="tw:text-xs tw:text-gray-600 tw:italic">
                      Note: Canceling the member automatically snoozes winbacks.
                    </p>
                    <div className="tw:flex tw:justify-between">
                      <Button
                        type="submit"
                        disabled={
                          !form.formState.isValid ||
                          requestAccountDropMutation.isPending
                        }
                        variant="destructive"
                      >
                        {requestAccountDropMutation.isPending
                          ? "Processing..."
                          : "Yes, proceed with cancel"}
                      </Button>
                      <Button
                        type="button"
                        variant="warning"
                        onClick={() => setShowForm(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </form>
              </Form>
            </div>
          )}
        </>
      )}
    </>
  )
}
